import React, { useState } from 'react';
import { 
  Building2, 
  Users, 
  Wallet, 
  Search, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  TrendingDown,
  LayoutDashboard,
  HardHat,
  ChevronDown,
  ChevronUp,
  LineChart,
  FileText,
  Lock,
  Zap
} from 'lucide-react';
import { MultiStepLeadForm } from './MultiStepLeadForm';
import { ConfirmationScreen } from './ConfirmationScreen';
import { TRANSLATIONS } from '../../translations';

import { Language } from '../../types';

interface Props { language: Language; }

export const OwnerPartnershipPage: React.FC<Props> = ({ language }) => {
  const [formCompleted, setFormCompleted] = useState(false);
  const t = TRANSLATIONS[language]?.ownerPartnership || TRANSLATIONS[Language.EN].ownerPartnership;
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const scrollToApply = () => {
    document.getElementById('op-apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    {
      q: t.faq.q1,
      a: t.faq.a1
    },
    {
      q: t.faq.q2,
      a: t.faq.a2
    },
    {
      q: t.faq.q3,
      a: t.faq.a3
    }
  ];

  return (
    <div className="w-full bg-white text-neutral-900 selection:bg-neutral-900 selection:text-white">
      {/* 1. Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-neutral-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2075&auto=format&fit=crop" 
            alt="Premium Architectural Home" 
            className="w-full h-full object-cover opacity-60 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-neutral-900/60 to-transparent"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center pt-20">
          <div className="w-full md:w-3/5 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs uppercase tracking-widest font-bold mb-6">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
              {t.hero.tagline}
            </div>
            <h1 className="text-5xl md:text-7xl font-heading uppercase tracking-widest text-white leading-[1.1] mb-6">
              {t.hero.title1}<br/><span className="text-neutral-400">{t.hero.title2}</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 font-light max-w-2xl leading-relaxed mb-10">
              {t.hero.desc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={scrollToApply}
                className="bg-white text-neutral-900 px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-neutral-200 transition-colors flex justify-center items-center gap-3"
              >
                {t.hero.cta} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Why This Model Exists */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2 relative">
              <div className="aspect-[4/3] bg-neutral-100 overflow-hidden relative">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop" 
                  alt="Collaboration between homeowner and project manager" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-6 border border-neutral-200 shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-neutral-900 flex items-center justify-center shrink-0">
                      <Users className="text-white" size={24} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-widest text-neutral-900 mb-1">{t.why.cardTitle}</h4>
                      <p className="text-xs text-neutral-600">{t.why.cardDesc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="w-full lg:w-1/2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.why.tagline}</h2>
              <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest mb-8 leading-tight">
                {t.why.title}
              </h3>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                {t.why.p1}
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed mb-10">
                {t.why.p2}
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <TrendingDown className="text-neutral-900 mb-3" size={32} />
                  <h4 className="font-bold text-neutral-900 mb-2">{t.why.b1Title}</h4>
                  <p className="text-sm text-neutral-500">{t.why.b1Desc}</p>
                </div>
                <div>
                  <ShieldCheck className="text-neutral-900 mb-3" size={32} />
                  <h4 className="font-bold text-neutral-900 mb-2">{t.why.b2Title}</h4>
                  <p className="text-sm text-neutral-500">{t.why.b2Desc}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. {t.process.title} (Timeline Diagram) */}
      <section className="py-24 px-6 md:px-12 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.process.tagline}</h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest">{t.process.title}</h3>
          </div>
          
          <div className="relative">
            {/* Horizontal connecting line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-neutral-300 z-0"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 relative z-10">
              {t.process.steps.map((item, i) => {
                const icons = [<Search size={24}/>, <FileText size={24}/>, <HardHat size={24}/>, <CheckCircle2 size={24}/>];
                return (
                <div key={i} className="flex flex-col items-center md:items-start relative group">
                  <div className="w-24 h-24 bg-white border border-neutral-200 shadow-sm flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform duration-300 relative z-10">
                    <span className="absolute top-2 left-2 text-[10px] font-bold text-neutral-400">0{i+1}</span>
                    <div className="text-neutral-900">{icons[i]}</div>
                  </div>
                  <h4 className="text-lg font-bold uppercase tracking-widest text-neutral-900 mb-3 md:text-left text-center">{item.title}</h4>
                  <p className="text-sm text-neutral-600 leading-relaxed md:text-left text-center">{item.desc}</p>
                </div>
              ); })}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Responsibilities Matrix */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.matrix.tagline}</h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest">{t.matrix.title}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-neutral-200">
            {/* Owner */}
            <div className="p-10 bg-neutral-50 border-b md:border-b-0 md:border-r border-neutral-200">
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-neutral-200">
                <h4 className="text-xl font-heading uppercase tracking-widest">{t.matrix.ownerTitle}</h4>
                <div className="w-10 h-10 bg-neutral-200 flex items-center justify-center">
                  <Lock size={20} className="text-neutral-600" />
                </div>
              </div>
              <ul className="space-y-6">
                {t.matrix.ownerItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <CheckCircle2 size={20} className="text-neutral-400 shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* {t.matrix.sharedTitle} */}
            <div className="p-10 bg-white border-b md:border-b-0 md:border-r border-neutral-200">
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-neutral-200">
                <h4 className="text-xl font-heading uppercase tracking-widest">{t.matrix.sharedTitle}</h4>
                <div className="w-10 h-10 bg-neutral-100 flex items-center justify-center border border-neutral-200">
                  <Users size={20} className="text-neutral-600" />
                </div>
              </div>
              <ul className="space-y-6">
                {t.matrix.sharedItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <Zap size={20} className="text-neutral-400 shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Le Sector */}
            <div className="p-10 bg-neutral-900 text-white">
              <div className="flex items-center justify-between mb-8 pb-4 border-b border-neutral-800">
                <h4 className="text-xl font-heading uppercase tracking-widest text-white">{t.matrix.lsTitle}</h4>
                <div className="w-10 h-10 bg-neutral-800 flex items-center justify-center">
                  <Building2 size={20} className="text-white" />
                </div>
              </div>
              <ul className="space-y-6">
                {t.matrix.lsItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <CheckCircle2 size={20} className="text-neutral-500 shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-300 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Funding Flow Diagram & 6. Project Journey (Visual Flow) */}
      <section className="py-24 px-6 md:px-12 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.flow.tagline}</h2>
              <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest mb-8 leading-tight">
                {t.flow.title}
              </h3>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                {t.flow.p1}
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed">
                {t.flow.p2}
              </p>
            </div>
            
            <div className="w-full lg:w-1/2 bg-white p-8 md:p-12 shadow-sm border border-neutral-200">
              {/* Visual Flow Diagram */}
              <div className="flex flex-col gap-6">
                {/* Step 1 */}
                <div className="flex items-center gap-6 p-4 bg-neutral-50 border border-neutral-200 relative">
                  <div className="w-12 h-12 bg-neutral-900 text-white flex items-center justify-center shrink-0">
                    <CheckCircle2 size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold text-neutral-900 uppercase tracking-widest text-sm mb-1">{t.flow.s1Title}</h5>
                    <p className="text-xs text-neutral-500">{t.flow.s1Desc}</p>
                  </div>
                  <div className="absolute -bottom-6 left-10 w-0.5 h-6 bg-neutral-300 z-10"></div>
                </div>
                
                {/* Step 2 */}
                <div className="flex items-center gap-6 p-4 bg-neutral-50 border border-neutral-200 relative">
                  <div className="w-12 h-12 bg-neutral-200 text-neutral-900 flex items-center justify-center shrink-0">
                    <FileText size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold text-neutral-900 uppercase tracking-widest text-sm mb-1">{t.flow.s2Title}</h5>
                    <p className="text-xs text-neutral-500">{t.flow.s2Desc}</p>
                  </div>
                  <div className="absolute -bottom-6 left-10 w-0.5 h-6 bg-neutral-300 z-10"></div>
                </div>
                
                {/* Step 3 */}
                <div className="flex items-center gap-6 p-4 bg-white border-2 border-neutral-900">
                  <div className="w-12 h-12 bg-white border-2 border-neutral-900 text-neutral-900 flex items-center justify-center shrink-0">
                    <Wallet size={24} />
                  </div>
                  <div>
                    <h5 className="font-bold text-neutral-900 uppercase tracking-widest text-sm mb-1">{t.flow.s3Title}</h5>
                    <p className="text-xs text-neutral-500">{t.flow.s3Desc}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Dashboard Illustration */}
      <section className="py-24 px-6 md:px-12 bg-neutral-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="w-full lg:w-1/2">
              <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.dash.tagline}</h2>
              <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest mb-8 leading-tight">
                {t.dash.title}
              </h3>
              <p className="text-lg text-neutral-400 leading-relaxed mb-8">
                {t.dash.desc}
              </p>
              
              <ul className="space-y-4">
                {t.dash.items.map((item, i) => (
                  <li key={i} className="flex items-center gap-4">
                    <LayoutDashboard size={20} className="text-neutral-500" />
                    <span className="text-sm text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="w-full lg:w-1/2 relative">
              {/* Abstract Dashboard Visual */}
              <div className="bg-neutral-800 p-6 md:p-8 border border-neutral-700 shadow-2xl rounded-sm transform lg:rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-neutral-700">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-neutral-500 uppercase tracking-widest">{t.dash.hub}</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-neutral-900 p-4 border border-neutral-700">
                    <div className="text-xs text-neutral-500 uppercase tracking-widest mb-2">{t.dash.budget}</div>
                    <div className="text-2xl text-white font-heading">42%</div>
                    <div className="w-full h-1 bg-neutral-800 mt-3">
                      <div className="w-[42%] h-full bg-white"></div>
                    </div>
                  </div>
                  <div className="bg-neutral-900 p-4 border border-neutral-700">
                    <div className="text-xs text-neutral-500 uppercase tracking-widest mb-2">{t.dash.schedule}</div>
                    <div className="text-2xl text-white font-heading">{t.dash.onTrack}</div>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-xs text-neutral-500">{t.dash.week}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-neutral-900 p-4 border border-neutral-700 h-40 relative overflow-hidden flex flex-col justify-end">
                  <div className="absolute top-4 left-4 text-xs text-neutral-500 uppercase tracking-widest">{t.dash.cashflow}</div>
                  <LineChart className="text-neutral-700 w-full h-full opacity-50" strokeWidth={1} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. {t.suit.title} (Icon Cards) */}
      <section className="py-24 px-6 md:px-12 bg-neutral-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.suit.tagline}</h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest">{t.suit.title}</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-10 md:p-12 border border-neutral-200">
              <div className="w-16 h-16 bg-neutral-900 text-white flex items-center justify-center mb-8">
                <CheckCircle2 size={32} />
              </div>
              <h4 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.suit.idealTitle}</h4>
              <ul className="space-y-4">
                {t.suit.idealItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-neutral-900 rounded-full shrink-0 mt-2"></span>
                    <span className="text-neutral-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white p-10 md:p-12 border border-neutral-200 opacity-60">
              <div className="w-16 h-16 bg-neutral-200 text-neutral-500 flex items-center justify-center mb-8">
                <Clock size={32} />
              </div>
              <h4 className="text-2xl font-heading uppercase tracking-widest mb-6">{t.suit.notTitle}</h4>
              <ul className="space-y-4">
                {t.suit.notItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-neutral-400 rounded-full shrink-0 mt-2"></span>
                    <span className="text-neutral-500">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ Accordion */}
      <section className="py-24 px-6 md:px-12 bg-white border-t border-neutral-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-500 mb-4">{t.faq.tagline}</h2>
            <h3 className="text-3xl md:text-5xl font-heading uppercase tracking-widest">{t.faq.title}</h3>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-neutral-200">
                <button 
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 bg-neutral-50 hover:bg-neutral-100 transition-colors focus:outline-none text-left"
                >
                  <span className="font-bold uppercase tracking-widest text-sm text-neutral-900 pr-8">{faq.q}</span>
                  {openFaq === index ? <ChevronUp size={20} className="text-neutral-500 shrink-0" /> : <ChevronDown size={20} className="text-neutral-500 shrink-0" />}
                </button>
                {openFaq === index && (
                  <div className="p-6 bg-white border-t border-neutral-200">
                    <p className="text-neutral-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. CTA Section with Lifestyle Imagery */}
      <section id="op-apply" className="relative py-32 px-6 md:px-12 bg-neutral-900 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=2070&auto=format&fit=crop" 
            alt="Beautiful finished interior" 
            className="w-full h-full object-cover opacity-20 mix-blend-luminosity"
          />
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 bg-white p-10 md:p-16 shadow-2xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading uppercase tracking-widest mb-6 text-neutral-900">
              {t.cta.title}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              {t.cta.desc}
            </p>
          </div>
          
          {formCompleted ? (
            <ConfirmationScreen language={language} />
          ) : (
            <MultiStepLeadForm onComplete={() => setFormCompleted(true)} language={language} />
          )}
        </div>
      </section>
    </div>
  );
};
